import { FieldError, RangeSlider, Select, Typography } from '@/ui-kit/';
import styles from './direction-info-registartion-form.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce, isArray } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type Profile } from '@/types/profile';
import { ButtonSubmitStep } from '../button-submit-step';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { RegionsIcon, SuccessNotificationIcon } from '@/assets/icons';
import {
    registrationStepFiveSchema,
    RegistrationStepFiveType
} from '@/schemes/registrationV4/registration-step-five-schema';
import clsx from 'clsx';
import { eventsApi } from '@/api';
import { FieldBlock } from '@/components/field-block';
import { useGetRegions } from '@/hooks/use-get-regions';
import { Region, SearchParamsRegions } from '@/api/region/types';
import { ANY_REGION_OPTION, ANY_REGION_OPTION_LABEL } from '@/utils/regions';
import { renderCity } from '@/utils/render-city';
import { SelectOption } from '@/ui-kit/select';
import { LocationProfile } from '@/components/popup-block/location-profile';
import { useTranslation } from 'react-i18next';
import { SignUpActions } from '@/components/sign-up-actions/sign-up-actions';
import layoutStyles from '../../form-registration.module.scss';

const DELAY_SEND_DATA = 1000;

interface Props {
    userData?: Profile | null;
    onUpdateUserData: (data: RegistrationStepFiveType) => Promise<any>;
    nextStep: () => void;
}

export const DirectionInfoRegistrationForm: React.FC<Props> = ({ userData, onUpdateUserData, nextStep }) => {
    const showToast = useToast((state) => state.show);
    useEffect(() => {
        callYaMetrika('new_dating_settings_screen4');
        eventsApi.updateUserEvent('new_dating_settings_screen4');
        eventsApi.updateUserEvent('registration_step_view_dating_settings_screen')
    }, []);

    const { t } = useTranslation();

    const initialValues = useMemo(() => {
        const getMinAge = () => {
            if (!userData) return 18;
            return userData.min_age !== null ? (userData.min_age >= 60 ? 60 : userData.min_age) : 18;
        };

        const getMaxAge = () => {
            if (!userData) return 60;
            return userData.max_age !== null ? (userData.max_age >= 60 ? 60 : userData.max_age) : 60;
        };

        return {
            regions: userData?.regions
                ? userData.regions.map((region) => ({
                        label: renderCity(region),
                        value: region.id,
                    }))
                : [
                    {
                        ...ANY_REGION_OPTION,
                        label: t(ANY_REGION_OPTION_LABEL)
                    }
                ],
            looks_for: userData?.looks_for || 'any',
            min_age: getMinAge(),
            max_age: getMaxAge(),
        };
    }, [userData]);
    
    const [isLooksForSelected, setIsLooksForSelected] = useState<Profile['looks_for']>(initialValues.looks_for);


    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm<RegistrationStepFiveType>({
        resolver: zodResolver(registrationStepFiveSchema),
        defaultValues: initialValues,
    });

    const {
        handleSubmit,
        formState: { errors, isDirty, isValid },
        setValue,
    } = methods;

    const onSubmit = async (data: RegistrationStepFiveType) => {
        try {
            setIsLoading(true);
            /*
             * for update only dirty data use code below
             * if (isDirty) {
             * 
             *     await onUpdateUserData({ state: 'complete', ...data });
             * }
             */
            await onUpdateUserData(data);

            nextStep();
            eventsApi.updateUserEvent('registration_step_complete_dating_settings_screen');
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeRangeAge = (value: any) => {
        methods.setValue('min_age', value[0]);
        methods.setValue('max_age', value[1]);
        eventsApi.updateUserEvent('registration_min_max_age');
        handleChangeUserDataDebounce({ min_age: value[0], max_age: value[1] });
    };

    const handleChangeUserDataDebounce = useCallback(
        debounce(async (data: any) => {
            await onUpdateUserData(data);
        }, DELAY_SEND_DATA),
        [onUpdateUserData],
    );

    const handleChangeLooksFor = (value: any) => {
        methods.setValue('looks_for', value);
        setIsLooksForSelected(value);
        handleChangeUserDataDebounce({ looks_for: value });
    };
    
    const [isPopupEditLocation, setPopupEditLocation] = useState(false)
    const locationHandler = async (regions: SelectOption[]) => {
        setPopupEditLocation(false);
        // @ts-ignore
        methods.setValue('regions', regions.map(
            (e: SelectOption) => e.value)
            .includes(
                ANY_REGION_OPTION.value
            )
            ? []
            : regions, { shouldValidate: true, shouldDirty: true }
        );

        await methods.trigger('regions');
        handleChangeUserDataDebounce({ region_ids: regions.map((item: { value: any }) => item.value) });
    };

    if (isPopupEditLocation) {
        return (
            <div className={styles.formContainer}>
                <LocationProfile
                    regions={userData?.regions || []}
                    onSubmit={locationHandler}
                    onClose={() => setPopupEditLocation(false)} />
            </div>
        );
    }

    return (
        <FormProvider {...methods}>
            <form
                className={styles.form}
                onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.formContainer}>
                    <Typography
                        variant="p"
                        fontWeight="medium"
                        className={styles.titleForm}
                        center>
                        {t('registration.specify_regions')}
                    </Typography>

                    <p className={styles.formInfoText}>
                        {t('registration.select_city_or_country')}
                    </p>

                    <div
                        id="regions_field"
                        className={
                            clsx(
                                styles.combobox,
                                styles.mb4,
                                styles.unfilledField
                            )
                        }
                        onClick={() => setPopupEditLocation(true)}>
                        <RegionsIcon />
                        <Typography
                            variant="label"
                            fontWeight="normal"
                            typeColor="gray"
                            className={
                                clsx(
                                    styles.cardSubtitle,
                                    styles.regionsLabel
                                )
                            }>
                            {initialValues.regions.length ? (
                                initialValues.regions.map(
                                    (item, index) =>
                                        <span key={index}>{item.label}</span>
                                )
                            ) : (
                                <span>{t('city_or_country_of_search')}</span>
                            )}

                        </Typography>
                    </div>

                    <FieldBlock>
                        <div className={styles.formGenderSearch}>
                            <div className={styles.formLabel}>
                                <Typography
                                    variant="label"
                                    fontWeight="bold"
                                    className={styles.formGenderTitle}
                                    isError={!!errors?.looks_for?.message}>
                                    {t('registration.who_show_you')}
                                </Typography>
                                <p className={styles.formGenderText}>
                                    {t('registration.сan_be_changed_at_any_time')}
                                </p>
                            </div>
                            <div className={styles.genderWrapper}>
                                <div
                                    className={clsx(
                                        styles.genderBtn,
                                        isLooksForSelected === 'female' && styles.genderBtnActive,
                                    )}
                                    onClick={() => handleChangeLooksFor('female')}>
                                    {t('registration.female_tertiary')}
                                </div>

                                <div
                                    className={clsx(
                                        styles.genderBtn,
                                        isLooksForSelected === 'male' && styles.genderBtnActive,
                                    )}
                                    onClick={() => handleChangeLooksFor('male')}>
                                    {t('registration.male_tertiary')}
                                </div>

                                <div
                                    className={clsx(
                                        styles.genderBtn,
                                        isLooksForSelected === 'any' && styles.genderBtnActive,
                                    )}
                                    onClick={() => handleChangeLooksFor('any')}>
                                    {t('optional')}
                                </div>
                            </div>
                        </div>
                    </FieldBlock>

                    <FieldBlock>
                        <div className={styles.formLabel}>
                            <Typography
                                variant="label"
                                fontWeight="bold"
                                className={styles.formGenderTitle}
                                isError={
                                    !!errors?.min_age?.message ||
                                    !!errors?.max_age?.message
                                }>
                                {t('age_range')}
                            </Typography>
                        </div>
                        <RangeSlider
                            maxStart={initialValues?.max_age}
                            minStart={initialValues?.min_age}
                            labels={[
                                t('from'),
                                t('to')
                            ]}
                            onChange={handleChangeRangeAge} />
                        <FieldError message={errors?.max_age?.message || errors?.min_age?.message} />
                    </FieldBlock>
                </div>
                <SignUpActions>
                    <ButtonSubmitStep
                        isLoading={isLoading}
                        stepNumber={5}
                        isDisabled={!isValid}
                        onSkip={nextStep} />
                </SignUpActions>
            </form>
        </FormProvider>
    );
};
